/**
 * NOVA Home by account state — the pure view model.
 *
 * Which Home a coach gets, the steps panel, sessions and "Needs you" view on
 * paid Home, and the win-back groups: each is a plain
 * function over data other surfaces already read, so the components only lay
 * the answers out. Statuses come resolved from the server (`getNovaAssets`);
 * nothing here derives one.
 *
 * Aliases: nova home view model, home variant, steps panel, needs you view, win-back groups
 */

import type { CoachAssetView, HeldAsset } from "~/lib/functions/nova-assets";
import type { CoachLifecycle } from "~/lib/shared/coach-account";
import { isOnBuildPath } from "~/lib/shared/nova/assets/build-path";
import type {
  AssetKey,
  CollectionAssetKey,
} from "~/lib/shared/nova/assets/catalog";
import {
  assetByKey,
  hasAssetPage,
  isCollectionAssetKey,
} from "~/lib/shared/nova/assets/catalog";
import type { AssetDestination } from "~/lib/shared/nova/assets/destination";
import {
  assetDestination,
  assetPageDestination,
} from "~/lib/shared/nova/assets/destination";
import type {
  NeedsYouRow,
  WaitingEnquiriesRead,
} from "~/lib/shared/nova/needs-you";
import { needsYouRows } from "~/lib/shared/nova/needs-you";
import { describeReadiness } from "~/lib/shared/readiness/describe";
import type { ReadinessResult } from "~/lib/shared/readiness/engine";
import type { CoachDailySession } from "~/lib/shared/types/session";

/**
 * Which Home a coach gets. `unavailable` is a Home with no account to read:
 * it says so and offers a retry, where a guessed variant could sell a trial to
 * a paying coach.
 */
export type NovaHomeVariant =
  | "unavailable"
  | "pre_trial"
  | "trial"
  | "paid"
  | "winback";

export function novaHomeVariant(
  lifecycle: CoachLifecycle | null,
): NovaHomeVariant {
  switch (lifecycle) {
    case null:
      return "unavailable";
    case "pre_trial":
      return "pre_trial";
    case "trial":
      return "trial";
    case "paid":
      return "paid";
    case "churned":
      return "winback";
    default: {
      const exhaustive: never = lifecycle;
      throw new Error(`Unknown coach lifecycle: ${String(exhaustive)}`);
    }
  }
}

/**
 * What pre-trial Home leads with, from the read of her scores. The score comes
 * first and the trial comes with her result, so Home offers the trial only
 * once she has a score.
 *
 * Only an answered read with no runs means she has no score. Until the read
 * answers nothing is offered, and a read that failed before it ever answered
 * offers only a retry: Home cannot tell yet whether she needs the score or
 * the trial. A later refetch that fails keeps the answer she already has.
 */
export type PreTrialStart =
  | { readonly kind: "loading" }
  | { readonly kind: "unreadable" }
  | { readonly kind: "no_score" }
  | { readonly kind: "scored"; readonly result: ReadinessResult };

export function preTrialStart(read: {
  /** The read's last answer; undefined until it has answered once. */
  readonly data:
    | { readonly runs: readonly { readonly result: ReadinessResult }[] }
    | undefined;
  readonly failed: boolean;
}): PreTrialStart {
  if (read.data === undefined) {
    return read.failed ? { kind: "unreadable" } : { kind: "loading" };
  }
  const newest = read.data.runs[0];
  return newest
    ? { kind: "scored", result: newest.result }
    : { kind: "no_score" };
}

/**
 * The line that names her score on pre-trial Home, in the Welcome card's
 * words. A version 1 result past $100K has no score, so there is no number
 * to quote.
 */
export function scoreGreeting(result: ReadinessResult): string {
  if (result.score === null) return "Your business is already past $100K.";
  const holdingBack = describeReadiness(result).components.find(
    (component) => component.isHoldingBack,
  );
  return holdingBack
    ? `Your score is ${result.score}. ${holdingBack.name} is holding you back.`
    : `Your score is ${result.score}.`;
}

// ── Steps panel ─────────────────────────────────────────────────────────────

export type HomeStepState = "done" | "now" | "not_started";

export interface HomeStep {
  readonly asset: CoachAssetView;
  readonly state: HomeStepState;
  /** Where the step opens: the asset's page, or the score's own page. */
  readonly destination: AssetDestination | null;
}

export interface StepsPanel {
  /** The build path in catalog order. */
  readonly steps: readonly HomeStep[];
  readonly done: number;
  readonly total: number;
  /** 0–100, rounded, for the bar. */
  readonly percent: number;
  /** "3 of 11 built". */
  readonly progressLabel: string;
  /** The first step not done. Null once every step is done. */
  readonly now: HomeStep | null;
}

/**
 * A step opens its asset's page. A `not_yet` asset has no click target of its
 * own, but its page is where the coach reads what it waits on, so the step
 * still links there.
 */
function stepDestination(asset: CoachAssetView): AssetDestination | null {
  const destination = assetDestination(asset);
  if (destination) return destination;
  return hasAssetPage(assetByKey(asset.key))
    ? assetPageDestination(asset.key)
    : null;
}

/**
 * The paid steps panel: the build path on My assets, in catalog order.
 *
 * A step is done when its asset is approved, and approval is the only way it
 * gets there. The first step that is not done is "now", so there is exactly
 * one while any work is left. What stays off the path is `isOnBuildPath`'s
 * call.
 */
export function buildStepsPanel(assets: readonly CoachAssetView[]): StepsPanel {
  const path = assets.filter(isOnBuildPath);
  const nowIndex = path.findIndex((asset) => asset.status !== "approved");
  const steps = path.map((asset, index): HomeStep => ({
    asset,
    state:
      asset.status === "approved"
        ? "done"
        : index === nowIndex
          ? "now"
          : "not_started",
    destination: stepDestination(asset),
  }));
  const done = steps.filter((step) => step.state === "done").length;
  const total = steps.length;
  return {
    steps,
    done,
    total,
    percent: total === 0 ? 0 : Math.round((done / total) * 100),
    progressLabel: `${done} of ${total} built`,
    now: nowIndex === -1 ? null : steps[nowIndex],
  };
}

/** The now step's button: a review when a version waits, else the page. */
export function stepActionLabel(step: HomeStep): string {
  return step.asset.status === "needs_review"
    ? `Review ${step.asset.label}`
    : `Open ${step.asset.label}`;
}

// ── Sessions ────────────────────────────────────────────────────────────────

/**
 * Her next coaching sessions, soonest first. One that is still running counts
 * as upcoming, the way the dashboard calendar keeps it until it ends.
 */
export function upcomingSessions(
  sessions: readonly CoachDailySession[],
  now: Date,
  limit: number,
): CoachDailySession[] {
  return sessions
    .filter((session) => new Date(session.endAt).getTime() > now.getTime())
    .sort(
      (a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime(),
    )
    .slice(0, limit);
}

// ── Needs you ───────────────────────────────────────────────────────────────

/** One read behind "Needs you": still loading, failed, or answered. */
export interface NeedsYouSource<T> {
  /** No answer yet, not even a failed one. */
  readonly pending: boolean;
  readonly failed: boolean;
  /** The last answer, kept when a later read fails. */
  readonly data: T | undefined;
}

export type NeedsYouView =
  | { readonly kind: "loading" }
  | {
      readonly kind: "ready";
      readonly rows: readonly NeedsYouRow[];
      /** A read failed, so rows may be missing: never say nothing waits. */
      readonly incomplete: boolean;
    };

/**
 * What the block can say. It waits for every read before it says anything,
 * so "nothing is waiting" is never an answer the reads have not given yet.
 * A failed read keeps the rows the others produced and marks the block
 * incomplete.
 */
export function needsYouView(input: {
  readonly waitingEnquiries: NeedsYouSource<WaitingEnquiriesRead>;
  readonly overdueInvoices: NeedsYouSource<number>;
  readonly assets: NeedsYouSource<readonly CoachAssetView[]>;
  readonly now: Date;
}): NeedsYouView {
  const sources = [input.waitingEnquiries, input.overdueInvoices, input.assets];
  if (sources.some((source) => source.pending)) return { kind: "loading" };
  return {
    kind: "ready",
    rows: needsYouRows({
      waitingEnquiries: input.waitingEnquiries.data ?? null,
      overdueInvoices: input.overdueInvoices.data ?? null,
      assets: input.assets.data ?? [],
      now: input.now,
    }),
    incomplete: sources.some((source) => source.failed),
  };
}

// ── Win-back ────────────────────────────────────────────────────────────────

export interface WinbackItem {
  readonly key: AssetKey;
  /** "Identity statement", or "3 LinkedIn posts" for a collection. */
  readonly text: string;
}

export interface WinbackGroups {
  /** Approved: saved for as long as the account exists. */
  readonly kept: readonly WinbackItem[];
  /** Written and never approved. */
  readonly frozen: readonly WinbackItem[];
}

const ITEM_NOUNS = {
  linkedin_posts: ["LinkedIn post", "LinkedIn posts"],
  articles: ["article", "articles"],
  client_stories: ["client story", "client stories"],
} as const satisfies Record<CollectionAssetKey, readonly [string, string]>;

function itemText(asset: HeldAsset, count: number): string {
  if (asset.cardinality === "singleton" || !isCollectionAssetKey(asset.key)) {
    return asset.label;
  }
  const [one, many] = ITEM_NOUNS[asset.key];
  return `${count} ${count === 1 ? one : many}`;
}

/**
 * Her work in the two groups the win-back panel lists, in catalog order. A
 * collection can sit in both: some posts approved, some never.
 */
export function winbackGroups(assets: readonly HeldAsset[]): WinbackGroups {
  return {
    kept: assets
      .filter((asset) => asset.approved > 0)
      .map((asset) => ({
        key: asset.key,
        text: itemText(asset, asset.approved),
      })),
    frozen: assets
      .filter((asset) => asset.unapproved > 0)
      .map((asset) => ({
        key: asset.key,
        text: itemText(asset, asset.unapproved),
      })),
  };
}

/** How many pieces of work the trial-cancel line names before "N more". */
const CANCEL_LINE_NAMED = 3;

/**
 * The line under "Trial cancels on [date]": the work she has saved and what
 * the end date changes, as a reminder, not an offer. It follows the win-back
 * rule, so it says what My assets will say after that date: approved work is
 * kept, written-but-unapproved work freezes, and nothing is deleted. Null
 * when she has built nothing.
 */
export function trialCancelWorkLine(
  assets: readonly HeldAsset[],
  endDate: string | null,
): string | null {
  if (assets.length === 0) return null;
  const items = assets.map((asset) =>
    itemText(asset, asset.approved + asset.unapproved),
  );
  const named = items.slice(0, CANCEL_LINE_NAMED);
  const rest = items.length - named.length;
  const list = new Intl.ListFormat("en", {
    style: "long",
    type: "conjunction",
  }).format(rest > 0 ? [...named, `${rest} more`] : named);

  const hasKept = assets.some((asset) => asset.approved > 0);
  const hasFrozen = assets.some((asset) => asset.unapproved > 0);
  const what =
    hasKept && hasFrozen
      ? "What you approved is kept, and unapproved drafts freeze as they are."
      : hasKept
        ? "Everything you approved is kept."
        : "Your unapproved drafts freeze as they are.";
  const when = endDate ? `After ${endDate}` : "When your trial ends";
  return `Saved so far: ${list}. ${when}, NOVA stops writing for you. ${what}`;
}
